import { useState, useEffect, useRef, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Activity, Zap, Clock, Shield, ArrowUpRight, Wifi, WifiOff, RefreshCw, Terminal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { tokenStore } from "@/lib/api";

const ENDPOINT_COLORS = {
  "/v1/random/bytes":    { bg: "bg-cyan-100",    text: "text-cyan-700",    dot: "bg-cyan-400" },
  "/v1/random/integer":  { bg: "bg-blue-100",    text: "text-blue-700",    dot: "bg-blue-400" },
  "/v1/random/float":    { bg: "bg-indigo-100",   text: "text-indigo-700",  dot: "bg-indigo-400" },
  "/v1/random/sequence": { bg: "bg-violet-100",   text: "text-violet-700",  dot: "bg-violet-400" },
  "/v1/random/uuid":     { bg: "bg-purple-100",   text: "text-purple-700",  dot: "bg-purple-400" },
  "/v1/vrf/generate":    { bg: "bg-amber-100",    text: "text-amber-700",   dot: "bg-amber-400" },
  "/v1/vrf/verify":      { bg: "bg-orange-100",   text: "text-orange-700",  dot: "bg-orange-400" },
};

const getColor = (ep) => ENDPOINT_COLORS[ep] || { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" };

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function formatCount(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const CHART_DATA_TEMPLATE = [
  { time: "00:00", rps: 420 }, { time: "04:00", rps: 180 }, { time: "08:00", rps: 680 },
  { time: "12:00", rps: 1240 }, { time: "16:00", rps: 960 }, { time: "20:00", rps: 720 }, { time: "Now", rps: 890 },
];

const MAX_EVENTS = 80;

export default function Dashboard() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectDelay = useRef(1000);
  const isMountedRef = useRef(true);

  const [wsStatus, setWsStatus] = useState("connecting");
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    total_calls: 0, total_bytes: 0, avg_latency_ms: 0, error_count: 0,
  });
  const [callsPerSecond, setCallsPerSecond] = useState(0);
  const lastCallsRef = useRef(0);
  const tableRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // CPS counter — use a ref so the interval doesn't recreate on every stats update
  const statsRef = useRef(stats);
  useEffect(() => { statsRef.current = stats; }, [stats]);

  useEffect(() => {
    const interval = setInterval(() => {
      const delta = statsRef.current.total_calls - lastCallsRef.current;
      lastCallsRef.current = statsRef.current.total_calls;
      setCallsPerSecond(delta);
    }, 1000);
    return () => clearInterval(interval);
  }, []); // empty deps — runs once on mount, cleaned up on unmount

  // WebSocket connection
  const connect = useCallback(() => {
    const token = tokenStore.getAccess();
    if (!token) return;

    const wsBase = process.env.REACT_APP_BACKEND_URL
      .replace("https://", "wss://")
      .replace("http://", "ws://");
    const url = `${wsBase}/api/ws/feed?token=${token}`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus("connected");
      reconnectDelay.current = 1000;
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === "call_event") {
          setEvents(prev => {
            const next = [{ ...msg.data, _ts: Date.now() }, ...prev];
            return next.slice(0, MAX_EVENTS);
          });
        } else if (msg.type === "stats" || msg.type === "connected") {
          setStats(s => {
            const next = {
              total_calls: msg.data.total_calls ?? s.total_calls,
              total_bytes: msg.data.total_bytes ?? s.total_bytes,
              avg_latency_ms: msg.data.avg_latency_ms ?? s.avg_latency_ms,
              error_count: msg.data.error_count ?? s.error_count,
            };
            // Seed lastCallsRef on first stats message so CPS doesn't spike
            if (lastCallsRef.current === 0 && next.total_calls > 0) {
              lastCallsRef.current = next.total_calls;
            }
            return next;
          });
        }
      } catch { /* ignore malformed */ }
    };

    ws.onclose = () => {
      if (!isMountedRef.current) return;
      setWsStatus("disconnected");
      reconnectRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        setWsStatus("reconnecting");
        reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, 30000);
        connect();
      }, reconnectDelay.current);
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    connect();
    return () => {
      isMountedRef.current = false;
      wsRef.current?.close();
      clearTimeout(reconnectRef.current);
    };
  }, [connect]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [events, autoScroll]);

  const statCards = [
    {
      label: "Total Requests", value: formatCount(stats.total_calls),
      icon: Activity, color: "text-cyan-600", bg: "bg-cyan-50",
      sub: `${callsPerSecond} calls/s`,
    },
    {
      label: "Entropy Delivered", value: formatBytes(stats.total_bytes),
      icon: Zap, color: "text-emerald-600", bg: "bg-emerald-50",
      sub: "cumulative today",
    },
    {
      label: "Avg Latency", value: `${stats.avg_latency_ms}ms`,
      icon: Clock, color: "text-blue-600", bg: "bg-blue-50",
      sub: "end-to-end P50",
    },
    {
      label: "API Health", value: stats.total_calls > 0
        ? `${(100 - (stats.error_count / stats.total_calls * 100)).toFixed(2)}%`
        : "100.00%",
      icon: Shield, color: "text-slate-600", bg: "bg-slate-50",
      sub: `${stats.error_count} errors`,
    },
  ];

  return (
    <div data-testid="customer-dashboard">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Welcome back, {user?.first_name}. Here's your quantum entropy overview.
          </p>
        </div>
        {/* WS Status */}
        <div className={`flex items-center gap-2 text-xs px-3 py-1.5 border rounded-full font-medium ${
          wsStatus === "connected" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
          wsStatus === "connecting" || wsStatus === "reconnecting" ? "text-amber-700 bg-amber-50 border-amber-200" :
          "text-red-700 bg-red-50 border-red-200"
        }`} data-testid="ws-status">
          {wsStatus === "connected" ? <Wifi className="w-3.5 h-3.5" /> :
           wsStatus === "connecting" || wsStatus === "reconnecting" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> :
           <WifiOff className="w-3.5 h-3.5" />}
          {wsStatus === "connected" ? "Live" : wsStatus === "reconnecting" ? "Reconnecting..." : wsStatus === "connecting" ? "Connecting..." : "Disconnected"}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats">
        {statCards.map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 p-5 hover:shadow-sm transition-shadow" data-testid={`stat-card-${i}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <div className="text-xs text-slate-400">{s.sub}</div>
            </div>
            <div className="text-2xl font-bold text-slate-900 font-mono tabular-nums mb-0.5">{s.value}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="dashboard-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Request Volume</h3>
              <p className="text-xs text-slate-500 mt-0.5">Last 24 hours (simulated)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={CHART_DATA_TEMPLATE} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="rpsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="time" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 4 }} />
              <Area type="monotone" dataKey="rps" stroke="#06b6d4" strokeWidth={2} fill="url(#rpsGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Entropy sources */}
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Entropy Health</h3>
          <div className="space-y-4">
            {[
              { label: "QRNG Primary", val: 98.7, color: "bg-cyan-500" },
              { label: "DRBG Buffer", val: 100.0, color: "bg-emerald-500" },
              { label: "VRF Pool", val: 94.2, color: "bg-blue-500" },
            ].map((s, i) => (
              <div key={i}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">{s.label}</span>
                  <span className="font-mono text-slate-900">{s.val}%</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${s.color} rounded-full transition-all`} style={{ width: `${s.val}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-100">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Compliance</div>
            <div className="flex flex-wrap gap-1.5">
              {["FIPS 140-3", "NIST SP 800-90B", "ISO 27001"].map(c => (
                <Badge key={c} variant="outline" className="text-[10px] py-0 border-slate-200 text-slate-600">{c}</Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Live Feed */}
      <div className="bg-white border border-slate-200" data-testid="live-feed">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Live API Feed</h3>
            {wsStatus === "connected" && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse inline-block" />
                streaming
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoScroll(a => !a)}
              className={`text-xs px-2.5 py-1 border rounded transition-colors ${autoScroll ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              data-testid="autoscroll-toggle"
            >
              Auto-scroll {autoScroll ? "on" : "off"}
            </button>
            <button onClick={() => setEvents([])} className="text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="clear-feed-btn">
              Clear
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-6 py-2 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
          <div className="col-span-1">Time</div>
          <div className="col-span-1">Req ID</div>
          <div className="col-span-3">Endpoint</div>
          <div className="col-span-1 text-center">Method</div>
          <div className="col-span-2">Tenant</div>
          <div className="col-span-1 text-right">Bytes</div>
          <div className="col-span-1 text-right">Latency</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1"></div>
        </div>

        {/* Rows */}
        <div ref={tableRef} className="overflow-y-auto" style={{ height: "360px" }} data-testid="live-feed-table">
          {events.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
              <div className="text-center">
                <Terminal className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>{wsStatus === "connected" ? "Waiting for events..." : "Connecting to live feed..."}</p>
              </div>
            </div>
          ) : (
            events.map((ev, i) => {
              const c = getColor(ev.endpoint);
              const ts = new Date(ev.timestamp);
              const timeStr = ts.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
              return (
                <div
                  key={`${ev.request_id}-${i}`}
                  className={`grid grid-cols-12 gap-2 px-6 py-2.5 text-xs border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${i === 0 ? "animate-[fadeIn_0.2s_ease]" : ""}`}
                  data-testid={`feed-row-${i}`}
                >
                  <div className="col-span-1 text-slate-400 font-mono tabular-nums">{timeStr}</div>
                  <div className="col-span-1 text-slate-400 font-mono truncate">{ev.request_id}</div>
                  <div className="col-span-3 flex items-center gap-1.5 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                    <span className={`font-mono truncate ${c.text}`}>{ev.endpoint}</span>
                  </div>
                  <div className="col-span-1 text-center">
                    <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                      {ev.method}
                    </span>
                  </div>
                  <div className="col-span-2 text-slate-600 truncate font-mono text-[11px]">{ev.tenant}</div>
                  <div className="col-span-1 text-right text-slate-500 font-mono tabular-nums">{formatBytes(ev.bytes)}</div>
                  <div className="col-span-1 text-right text-slate-500 font-mono tabular-nums">{ev.latency_ms}ms</div>
                  <div className="col-span-1 text-center">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      ev.status === 200 ? "text-emerald-700 bg-emerald-50" :
                      ev.status === 429 ? "text-amber-700 bg-amber-50" :
                      "text-red-700 bg-red-50"
                    }`}>
                      {ev.status}
                    </span>
                  </div>
                  <div className="col-span-1" />
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-400">
            Showing last {events.length} events &mdash; Platform QRNG traffic
          </p>
          <div className="flex items-center gap-4 text-[10px] text-slate-400 uppercase tracking-wider">
            {Object.entries(ENDPOINT_COLORS).slice(0, 4).map(([ep, c]) => (
              <span key={ep} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                {ep.replace("/v1/", "").replace("/", " ")}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
